import { useState } from 'react';
import {
    useNavigate,
    useLocation,
    useParams,
    Navigate,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import useUpdateSRTService from './queries/useUpdateSRTService';

import {
    LoadingIndicator,
    Alert,
    AlertIcon,
    AlertDescription,
} from '@/components/v4';

import FormBase from '@/components/forms/new/FormBase';
import BreadcrumbTrail from 'components/BreadcrumbTrail';
import SBCaaSRegionsField from '../../serviceprofiles/edit/formFields/SBCaaSRegionsField';
import SRTMaxChannelField from '../../serviceprofiles/edit/formFields/SRTMaxChannelField';
import useGetSRTProfileMaxChannel from './queries/useGetSRTProfileMaxChannel';
import useGetSRTServiceRegions from './queries/useGetSRTServiceRegions';
import useGetSRTService from './queries/useGetSRTService';
import { SBCAAS_MODERN_LABEL } from '@/constants/sbcaas';

export function SRT() {
    const location = useLocation();
    const { state } = location;
    const navigate = useNavigate();
    const { id } = useParams();

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const companyID = useSelector(
        (state) => state.navigationLists.currentCompany,
    );
    const { darkMode } = useSelector((state) => state.settings);

    const updateService = useUpdateSRTService();

    const handleSubmit = async (values, actions) => {
        setIsLoading(true);
        setError(null);

        try {
            const transformedRegions = values.regions.map((region) => ({
                id: region.value,
            }));

            const params = {
                id: selectedServiceData.id,
                regions: transformedRegions,
                // PUT /Service/SRT replaces the definition, so carry the
                // existing trunk types through — this form doesn't edit them.
                trunkTypes: selectedServiceData.trunkTypes ?? [],
                maxChannel: Number(values.maxChannel),
                companyID,
                name: values.name,
            };

            await updateService.mutateAsync(params);
            toast.success(
                `${SBCAAS_MODERN_LABEL} service updated successfully.`,
            );
            setTimeout(() => navigate('/dashboard-services'), 1000);
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                    'An error occurred while updating the service.',
            );
            actions.setSubmitting(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackButton = () => navigate('/dashboard-services');

    const { data: availableRegions = [], isLoading: regionsLoading } =
        useGetSRTServiceRegions({ companyID });

    // A service cannot allocate more channels than its profile allows (2240)
    const { data: profileMaxChannel = null } = useGetSRTProfileMaxChannel({
        companyID,
    });

    const { data: selectedServiceData, isLoading: serviceDataLoading } =
        useGetSRTService({ id });

    if (!state?.serviceId) {
        return <Navigate to="/dashboard-services" />;
    }

    if (regionsLoading || serviceDataLoading || !selectedServiceData) {
        return <LoadingIndicator />;
    }

    const mapToOptions = (values, options) => {
        if (!values || !options) return [];
        if (Array.isArray(values)) {
            return (
                values
                    .map((v) =>
                        options.find((o) => o.value === (v.value ?? v.id ?? v)),
                    )
                    ?.filter(Boolean) || []
            );
        }
        return (
            options.find(
                (o) => o.value === (values.value ?? values.id ?? values),
            ) || null
        );
    };

    const initialValues = {
        name: selectedServiceData?.name || '',
        regions: mapToOptions(selectedServiceData?.regions, availableRegions),
        maxChannel: selectedServiceData?.maxChannel || 10,
    };

    const breadCrumbTrail = [
        { label: 'Services', path: ['/dashboard-services', { index: 0 }] },
        { label: `Edit ${SBCAAS_MODERN_LABEL} Service`, path: '' },
    ];

    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`Edit ${SBCAAS_MODERN_LABEL} Service`}
                subTitleHeader={true}
                initialValues={initialValues}
                finalFormSubmit={handleSubmit}
                customClick={true}
                onBack={handleBackButton}
                isLoading={isLoading}
                enableReinitialize={true}>
                {() => [
                    <SBCaaSRegionsField
                        key="regions"
                        options={availableRegions}
                        darkMode={darkMode}
                    />,
                    <SRTMaxChannelField
                        key="maxChannel"
                        darkMode={darkMode}
                        maxAllowed={profileMaxChannel}
                    />,
                    error && (
                        <Alert
                            key="error"
                            status="error"
                            sx={{ marginTop: '1rem' }}>
                            <AlertIcon />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    ),
                ]}
            </FormBase>
        </>
    );
}
